// src/config/qualityCost.ts

/**
 * Configuración técnica por resolución.
 *
 * ✅ Este archivo NO calcula créditos.
 * ✅ Los créditos (y multiplicadores por plan) viven en plans.ts:
 *    - costCreditsForResolution(plan, resolution)
 *
 * Aquí solo guardamos:
 * - resolución -> label UI
 * - resolución -> tamaño real (width x height)
 * - resolución -> motor (Replicate / OpenAI)
 * - resolución -> modelo por defecto (si aplica)
 */

import type { ResolutionKey } from "./plans";
import { RESOLUTION_LABELS } from "./plans";

export type { ResolutionKey } from "./plans";

/**
 * Motores soportados actualmente.
 */
export type EngineKey = "replicate" | "openai";

export interface ResolutionConfig {
  key: ResolutionKey;
  label: string;
  width: number;
  height: number;
  engine: EngineKey;

  // ✅ Opcional (en OpenAI no lo necesitas realmente)
  defaultModelSlug?: string;
}

/**
 * Configuración central de resoluciones (técnica).
 * OJO: no incluir créditos aquí para evitar duplicar lógica.
 */
export const RESOLUTIONS_CONFIG: Record<ResolutionKey, ResolutionConfig> = {
  "720p": {
    key: "720p",
    label: RESOLUTION_LABELS["720p"],
    width: 1280,
    height: 720,
    engine: "replicate",
    defaultModelSlug: "stability-ai/sdxl-light",
  },
  "1080p": {
    key: "1080p",
    label: RESOLUTION_LABELS["1080p"],
    width: 1920,
    height: 1080,
    engine: "replicate",
    defaultModelSlug: "black-forest-labs/flux-schnell",
  },
  "2k": {
    key: "2k",
    label: RESOLUTION_LABELS["2k"],
    width: 2560,
    height: 1440,
    engine: "replicate",
    defaultModelSlug: "stability-ai/sdxl-highres",
  },
  "4k": {
    key: "4k",
    label: RESOLUTION_LABELS["4k"],
    width: 3840,
    height: 2160,
    engine: "openai",
  },
  "8k": {
    key: "8k",
    label: RESOLUTION_LABELS["8k"],
    width: 7680,
    height: 4320,
    engine: "openai",
  },
};

export function getResolutionConfig(key: ResolutionKey): ResolutionConfig {
  return RESOLUTIONS_CONFIG[key];
}

